<template>
  <div class="mb-2">
    <div v-if="hasPromotionApplied" class="flex bg-gray-05 p-2 items-start justify-between">
      <div class="flex gap-x-1">
        <pdl-icon name="local_offer" />
        <div class="flex flex-col">
          <span class="font-medium text-md">{{ promotionId }}</span>
          <span v-if="promotionName" class="text-sm">{{ promotionName }}</span>
        </div>
      </div>
      <pdl-button
        qaid="promotion-remove-button"
        class="promotion-remove-button"
        :disabled="isSubmittingPromotion"
        tertiary
        icon-only
        icon="remove_circle_outline"
        :label="$t('text.remove')"
        @click="handlePromotionRemove(promotionId)"
      />
    </div>
    <pdl-collapse
      v-else
      qaid="promo-collapse"
      header-classes="promotions"
      content-classes="flex flex-col bg-gray-05 p-2"
      default-active
    >
      <pdl-collapse-item>
        <template #title>
          <div id="promo-heading" class="text-md font-medium">
            <pdl-icon name="local_offer" />
            <span>{{ $t('basket.couponCode.havePromo') }}</span>
          </div>
        </template>
        <form
          id="promoSubmit"
          class="form-group--inline gap-x-4"
          aria-labelledby="promo-heading"
          @submit.prevent="handlePromotionSubmit(couponCode)"
        >
          <pdl-form-group
            v-model="couponCode"
            name="couponCode"
            :placeholder="$t('basket.couponCode.placeholder')"
            class="mb-0 w-full"
            qaid="promo-code-input"
            autocomplete="off"
          >
            <template #label>
              <label for="couponCode" class="sr-only">{{ $t('text.promotions') }}</label>
            </template>
          </pdl-form-group>
          <pdl-button qaid="promo-apply-button" primary form-inline :disabled="isSubmittingPromotion" type="submit">
            {{ $t('text.button.apply') }}
          </pdl-button>
        </form>
      </pdl-collapse-item>
    </pdl-collapse>
  </div>
</template>

<script>
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlButton} from '@pedal/pdl-button';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {mapState, mapGetters, mapActions, mapMutations} from 'vuex';
import {PdlToastType} from '@/constants/pdl-toast-type';
import {scrollToTop} from '@/utils/scroll-to.js';

export default {
  components: {
    PdlFormGroup,
    PdlButton,
    PdlIcon,
    PdlCollapse,
    PdlCollapseItem,
  },
  props: {
    hasMixedPromoItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      couponCode: '',
      isSubmittingPromotion: false,
    };
  },
  computed: {
    ...mapState('cart', ['cartData']),
    ...mapGetters('cart', ['hasDeliveryModeSelectionsMade']),
    hasPromotionApplied() {
      return Boolean(this.cartData?.appliedVouchers[0]?.code);
    },
    promotionId() {
      return this.cartData?.appliedVouchers[0]?.voucherCode;
    },
    promotionName() {
      return this.cartData?.appliedVouchersList?.[0]?.name;
    },
  },
  watch: {
    cartData() {
      this.checkForInvalidPromoVouchers();
    },
  },
  methods: {
    ...mapMutations('cart', ['setHasClickedProceedWithoutDeliveryModeSelected']),
    ...mapActions('cart', ['applyPromotion', 'removePromotion']),
    checkForInvalidPromoVouchers() {
      if (!this.cartData?.appliedVouchers.length) return;

      const hasOrderPromos = this.cartData?.appliedOrderPromotions?.length > 0;
      const hasProductPromos = this.cartData?.appliedProductPromotions?.length > 0;

      if (!hasOrderPromos && !hasProductPromos) {
        this.handlePromotionRemove(this.cartData.appliedVouchers[0].voucherCode);
      }
    },
    async handlePromotionSubmit(couponCode) {
      if (!this.hasDeliveryModeSelectionsMade) {
        this.setHasClickedProceedWithoutDeliveryModeSelected(true);
        this.couponCode = '';
        return;
      }
      const trimmedCode = couponCode.trim();
      const couponName = `'${trimmedCode}'`;
      const errMessage = this.$t('basket.couponCode.error.notapplied', [couponName]);
      await this.handlePromotion(
        () => this.applyPromotion(trimmedCode),
        errMessage,
        this.$t('basket.couponCode.applied', [couponName])
      );
    },
    async handlePromotionRemove(promotionId) {
      const promotionName = `'${promotionId}'`;
      const errMessage = this.$t('basket.couponCode.error.notremoved', [promotionName]);
      await this.handlePromotion(
        () => {
          this.setHasClickedProceedWithoutDeliveryModeSelected(true);
          return this.removePromotion(promotionId);
        },
        errMessage,
        this.$t('basket.couponCode.removed', [promotionName])
      );
    },
    async handlePromotion(promoFunc, defaultErrorMessage, defaultSuccessMessage) {
      this.isSubmittingPromotion = true;
      try {
        await promoFunc();
        this.$notify({
          type: PdlToastType.SUCCESS,
          message: this.hasMixedPromoItems ? this.$t('basket.couponCode.success.message') : defaultSuccessMessage,
          ...(this.hasMixedPromoItems && {duration: 0}),
          showClose: true,
        });
        scrollToTop();
      } catch (error) {
        this.$notify({
          type: PdlToastType.ERROR,
          message: defaultErrorMessage,
          showClose: true,
        });
      } finally {
        this.couponCode = '';
        this.isSubmittingPromotion = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
:deep(.pdl-collapse-item__wrap),
:deep(.pdl-collapse-item__header) {
  border-bottom: none;
}

:deep(.promotions) {
  background-color: var(--gray-05);
  padding: 1rem 0.5rem 0.5rem 1rem;
}

.promotion-remove-button {
  padding: 0.25rem;
}
</style>
